import { EPIC_MOBILE_PUBLIC_ORIGIN } from "./environment";
import { safeDestination } from "../../public/shared/socials/socialLinkWidget.js";
import type { PublicAccountProfile, PublicProfileSection, SocialLink } from "./types";

function text(value: unknown, limit: number) {
  return typeof value === "string" ? value.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, "").trim().slice(0, limit) : "";
}

export function publicProfileUrl(username: string) {
  return /^[A-Za-z0-9][A-Za-z0-9._-]{0,119}$/.test(username) && username !== "." && username !== ".."
    ? `${EPIC_MOBILE_PUBLIC_ORIGIN}/${encodeURIComponent(username)}/` : null;
}

export function profileImageUrl(value: unknown) {
  const raw = text(value, 2048);
  if (!raw) return null;
  try {
    const url = new URL(raw, EPIC_MOBILE_PUBLIC_ORIGIN);
    return url.protocol === "https:" && !url.username && !url.password ? url.href : null;
  } catch (_) { return null; }
}

export function normalizePublicProfile(value: unknown, accountUsername: string): PublicAccountProfile {
  const source = value && typeof value === "object" ? value as Record<string, any> : {};
  const username = text(source.username, 120);
  if (!username || username.toLowerCase() !== accountUsername.toLowerCase()) {
    throw Object.assign(new Error("The returned profile does not match your active account."), { code: "PUBLIC_PROFILE_ACCOUNT_MISMATCH" });
  }
  const sections = Object.fromEntries((["socialLinks", "companion", "content", "merch", "inventory"] as PublicProfileSection[])
    .map(key => [key, source.widgetConfig?.[key]?.enabled !== false])) as Record<PublicProfileSection, boolean>;
  const socialLinks: SocialLink[] = (Array.isArray(source.socialLinks) && sections.socialLinks ? source.socialLinks : [])
    .slice(0, 25).filter((item: any) => item && item.visible !== false && safeDestination(text(item.url, 2048)))
    .map((item: any, index: number) => ({
      id: text(item.id, 120) || `public-social-${index}`,
      platform: text(item.platform, 60), label: text(item.label, 80),
      url: safeDestination(text(item.url, 2048))!, visible: true,
      imageAssetId: /^social_image_[a-f0-9]{32}$/.test(item.imageAssetId) ? item.imageAssetId : null,
      size: ["small", "medium", "large"].includes(item.size) ? item.size : "medium"
    }));
  const collection = (key: "content" | "merch" | "inventory") =>
    (sections[key] && Array.isArray(source[key]) ? source[key] : []).slice(0, 100)
      .filter((item: any) => item && typeof item === "object")
      .map((item: any) => ({ title: text(item.title || item.name, 200) || "Untitled" }));
  return {
    username, displayName: text(source.displayName, 120) || username,
    type: text(source.type, 40), bio: text(source.bio, 4000),
    avatarUrl: profileImageUrl(source.avatarUrl), sections, socialLinks,
    content: collection("content"), merch: collection("merch"), inventory: collection("inventory")
  };
}
